<template>
  <div class="d-flex flex-row w-100 h-100 registers-layout">
    <div class="registers-section d-flex flex-column">
      <!-- Header (matching RegisterManagement style) -->
      <v-list-item
        two-line
        :class="$vuetify.theme.isDark ? 'bg2' : 'white'"
        class="register-header">
        <v-list-item-avatar size="40" color="primary" class="mr-3">
          <v-icon color="white">mdi-table</v-icon>
        </v-list-item-avatar>
        <v-list-item-content>
          <v-list-item-title class="register-header__title">
            {{ registerName || 'Register' }}
          </v-list-item-title>
          <v-list-item-subtitle class="register-header__subtitle">
            View and manage register data
          </v-list-item-subtitle>
        </v-list-item-content>
        <v-list-item-action class="register-header__actions">
          <v-btn
            v-if="canViewSyncActivity"
            small
            text
            color="primary"
            title="View relationship sync activity for this register"
            aria-label="View relationship sync activity for this register"
            @click="openRegisterSyncActivity">
            <v-icon left small>mdi-history</v-icon>
            Sync Activity
          </v-btn>
          <AuditHistoryButton
            label="Audit History"
            title="View Register Audit History"
            :is-active="auditSidebarOpen"
            :entry-count="auditLogsTotal"
            @click="openRegisterAuditHistory" />
        </v-list-item-action>
      </v-list-item>
      <v-divider />
      <keep-alive>
        <router-view
          class="flex-grow-1"
          :include="['app-register']"
          ref="registerView"
           :currentRegisterType="currentRegisterType"
           :register-info="currentRegister"
           @showIn3d="showRegistersOn3d"
           @filter-snapshot-change="onFilterSnapshotChange"
           @show-viewers-section="onShowViewersSection"
          @edit-completed="handleEditComplete($event)"></router-view>
      </keep-alive>
    </div>
    <v-sheet
      v-show="viewerOpened"
      class="viewers-section-wrapper"
      style="width: 30vw; min-width: 400px; flex-shrink: 0;">
      <ViewersSection
        ref="viewersSection"
         :viewerTab.sync="viewerTab"
         :usePhotoSphereViewer="false"
         @expand="expandToMainViewer"
         @closeViewersSection="onCloseViewer"
      />
    </v-sheet>

    <!-- Audit History Sidebar.
         Overlay (float over content) only when the 3D/2D/360 viewer is open —
         that's the case where data table + viewer + sidebar would overflow the row.
         When the viewer is closed, push behavior leaves room for both. -->
    <RegisterAuditSidebar
      :register-id="currentRegisterType"
      :register-name="registerName"
      :overlay="viewerOpened" />
  </div>
</template>

<script>
  import ViewersSection from '@Dashboard/components/ViewersSection.vue';
  import { RegisterAuditSidebar, AuditHistoryButton } from '../components/audit';
  import { mapGetters, mapActions } from 'vuex';
  import permissions from '@Share/config/permissions';
  import { $has } from '@/utils';
  import { log, warn, error } from '@/utils/debugLogger';
  import registerService from '../services/registerService';
  import { API_LIMITS } from '@/constants/apiLimits';
  import {
    REGISTER_VIEWER_ACTION,
    getRegisterTagViewPermission,
    getRegisterViewerContext,
    tagBelongsToRegisterPanel,
  } from '../config/registerViewerContext';

  export default {
    name: 'main-registers',
    data() {
      return {
        loadType: false,
        selectedFilter: [0],
        timerId: null,
        permissions,
        is3dOpened: false,
        viewerOpened: false,
        viewerTab: '3D View',
        isolatedTags: [],
        currentRequestController: null,
        currentFilterSnapshot: null,
        isResolvingRegisterFilter: false,
      };
    },
    computed: {
      ...mapGetters('REGISTERS', {
        isLoading: 'GET_LOADING',
        entries: 'GET_REGISTERS',
        auditSidebarOpen: 'GET_AUDIT_SIDEBAR_OPEN',
        auditLogsTotal: 'GET_AUDIT_LOGS_TOTAL',
      }),
      currentRegisterType() {
        return this.$route.params.registerId;
      },
      currentRegister() {
        return this.entries.find((r) => r.id === this.currentRegisterType) || null;
      },
      registerName() {
        return this.currentRegister?.name || '';
      },
      facilityId() {
        return this.$route.params.id;
      },
      canViewSyncActivity() {
        return this.$has(permissions.Admin.Access);
      },
      options: {
        get() {
          return this.$store.getters['REGISTERS/GET_OPTIONS'];
        },
        set(val) {
          this.$store.commit('REGISTERS/SET_OPTIONS', val);
        },
      },
    },
    watch: {
      viewerTab() {
        if (this.viewerOpened) {
          this.loadViewerForCurrentTab();
        }
      },
      currentRegisterType() {
        this.currentFilterSnapshot = null;
        this.currentRequestController?.abort();
      },
    },
    beforeDestroy() {
      this.cancelCurrentRequest();
    },
    mounted() {
      this.openPendingLinkedTagsIn3d();
    },
    methods: {
      $has,
      ...mapActions('REGISTERS', {
        openAuditSidebarRegister: 'OPEN_AUDIT_SIDEBAR_REGISTER',
      }),

      /**
       * Opens the audit sidebar in register mode to show all changes
       * for the current register (row changes, imports, exports, etc.)
       */
      openRegisterAuditHistory() {
        if (!this.currentRegisterType) {
          warn('MainRegisters', 'No register selected');
          return;
        }
        this.openAuditSidebarRegister({
          registerId: this.currentRegisterType
        });
      },

      openRegisterSyncActivity() {
        if (!this.facilityId || !this.currentRegisterType) return;

        this.$router.push({
          path: `/${this.facilityId}/sync-activity`,
          query: { registerId: this.currentRegisterType },
        });
      },

      cancelCurrentRequest() {
        // Clear any pending timeout
        if (this.timerId !== null && this.timerId !== -1) {
          clearTimeout(this.timerId);
          this.timerId = null;
        }
        this.currentRequestController?.abort();
        this.currentRequestController = null;
      },

      onFilterSnapshotChange(snapshot) {
        this.currentFilterSnapshot = snapshot ? { ...snapshot } : null;
      },

      async collectFilteredLinkedTags(snapshot, signal) {
        const registerId = this.currentRegisterType;
        const facilityId = this.$route.params.id;
        const pageSize = API_LIMITS.REGISTER_FILTER_PAGE_SIZE;
        const tagsById = new Map();
        let page = 1;
        let totalCount = null;

        while (totalCount === null || (page - 1) * pageSize < totalCount) {
          const response = await registerService.getRegisterTableData(registerId, {
            page,
            itemsPerPage: pageSize,
            facilityId,
            ...(snapshot.search ? { search: snapshot.search } : {}),
            ...(Object.keys(snapshot.columnFilters || {}).length
              ? { columnFilters: { ...snapshot.columnFilters } }
              : {}),
            ...(snapshot.relationshipAttentionFilter || {}),
          }, signal);

          if (signal.aborted
            || this.currentRegisterType !== registerId
            || this.currentFilterSnapshot?.requestId !== snapshot.requestId) {
            return null;
          }

          const rows = response?.rows || [];
          rows.forEach((row) => {
            (row.LinkedTags || []).forEach((tag) => {
              if (tag?.id) tagsById.set(tag.id, tag);
            });
          });

          totalCount = Number(response?.totalCount) || 0;
          if (!rows.length) break;
          page += 1;
        }

        return [...tagsById.values()];
      },

      async expandToMainViewer({ activeTab, panoramaTransfer } = {}) {
        const registerId = this.currentRegisterType;
        let register = this.currentRegister;
        if (!register) {
          try {
            register = await registerService.getRegister(registerId);
            if (this.currentRegisterType !== registerId) return;
          } catch (err) {
            error('RegistersIndex', 'Failed to resolve the current Register before opening the main viewer:', err);
            this.$dialog.notify.error('Failed to load the current Register. Please try again.');
            return;
          }
        }

        const context = getRegisterViewerContext(register?.type);
        if (!context) {
          this.$dialog.notify.warning('Choose a supported Register Type before opening the tag list.');
          return;
        }
        if (!this.$has(context.permissions, true)) {
          this.$dialog.notify.warning(`You do not have permission to view the ${context.name} list.`);
          return;
        }

        const snapshot = this.currentFilterSnapshot;
        if (snapshot?.hasFilters && snapshot.status !== 'ready') {
          this.$dialog.notify.info('Please wait for the current Register filter to finish loading.');
          return;
        }

        this.currentRequestController?.abort();
        const controller = new AbortController();
        this.currentRequestController = controller;
        this.isResolvingRegisterFilter = true;

        try {
          const hasFilter = snapshot?.status === 'ready' && snapshot.hasFilters === true;
          const tags = hasFilter
            ? await this.collectFilteredLinkedTags(snapshot, controller.signal)
            : null;
          if (controller.signal.aborted || (tags === null && hasFilter)) return;
          const scopedTags = (tags || []).filter((tag) => {
            if (!tagBelongsToRegisterPanel(tag, context.registerType)) return false;
            const tagPermission = getRegisterTagViewPermission(tag);
            return Boolean(tagPermission && this.$has(tagPermission));
          });

          const viewerType = {
            '360 View': '360',
            '2D View': '2d',
            '3D View': '3d',
          }[activeTab] || '3d';

          this.$store.commit('TAGS_REGISTER/SET_ISOLATED_TAGS', this.isolatedTags);
          await this.$store.dispatch('VIEWERS_MODE/START_VIEWER_MODE', {
            mode: 'register-fullscreen',
            viewer: viewerType,
            button: 'Back to Register',
            action: REGISTER_VIEWER_ACTION,
            metadata: {
              registerId,
              registerName: register.name || this.registerName,
              registerType: context.registerType,
              filterRequestId: hasFilter ? snapshot.requestId : null,
              hasFilter,
              tags: scopedTags,
              tagIds: scopedTags.map((tag) => tag.id),
            },
            panoramaTransfer,
          });
        } catch (err) {
          if (err?.name === 'AbortError' || err?.name === 'CanceledError') return;
          error('RegistersIndex', 'Failed to apply the Register filter in the main viewer:', err);
          this.$dialog.notify.error('Failed to open the filtered tag list. Please try again.');
        } finally {
          if (this.currentRequestController === controller) {
            this.currentRequestController = null;
            this.isResolvingRegisterFilter = false;
          }
        }
      },

      openPendingLinkedTagsIn3d() {
        if (this.$route.query?.showLinkedTagsIn3d !== '1') return;

        const tags = this.$store.getters['TAGS_REGISTER/GET_ISOLATED_TAGS'] || [];
        this.$store.commit('TAGS_REGISTER/SET_ISOLATED_TAGS', []);
        if (tags.length) this.showRegistersOn3d(tags);

        const query = { ...this.$route.query };
        delete query.showLinkedTagsIn3d;
        this.$router.replace({ query }).catch((err) => {
          if (err?._isRouter !== true && err?.name !== 'NavigationDuplicated') {
            error('RegistersIndex', 'Failed to clear the pending 3D request:', err);
          }
        });
      },

      showRegistersOn3d(tags) {
        if (!tags || (Array.isArray(tags) && tags.length === 0)) {
          return;
        }

        if (!(tags instanceof Array)) {
          tags = [tags];
        }

        this.isolatedTags = tags;
        this.viewerOpened = true;
        this.is3dOpened = true;
        this.viewerTab = '3D View';
        // Clear stale P&ID from previous tag (ViewersSection's tagsRegisterIsolatedTags
        // watcher doesn't apply here since Registers uses local isolatedTags, not TAGS_REGISTER)
        if (this.$store.state.TAGS?.activeDocument) {
          this.$store.commit('SET_ACTIVE_DOCUMENT', null);
        }

        this.$nextTick(() => {
          this.isolateTagsIn3dViewer();
        });
      },

      /**
       * Handle show-viewers-section event from child components (e.g. P&ID button)
       * Opens the viewers section and switches to the requested tab
       */
      onShowViewersSection(tabName) {
        this.viewerOpened = true;
        this.viewerTab = tabName;
      },

      /**
       * Load the appropriate viewer based on the currently selected tab
       */
      loadViewerForCurrentTab() {
        if (!this.viewerOpened) return;

        // For Document tab, delegate to ViewersSection (which handles activeDocument state)
        if (this.viewerTab === 'Document') {
          this.$refs.viewersSection?.loadDocumentViewer?.();
          return;
        }

        // For other tabs, require isolatedTags
        if (!this.isolatedTags.length) return;

        switch (this.viewerTab) {
          case '3D View':
            this.isolateTagsIn3dViewer();
            break;
          case '2D View':
            this.isolateTagsIn2dViewer();
            break;
          case '360 View':
            this.isolateTagsIn360Viewer();
            break;
          default:
            warn('RegistersIndex', 'loadViewerForCurrentTab: Unknown tab', this.viewerTab);
        }
      },

      /**
       * Isolate tags in the 3D viewer
       */
      async isolateTagsIn3dViewer() {
        /* AVA-492 explicit-viewer */
        const requestedTags = this.isolatedTags;
        const tags = await this.hydrateTagsFor3d(requestedTags);

        // A newer row may have been selected while the tag details were loading.
        if (this.isolatedTags !== requestedTags) return;
        this.isolatedTags = tags;

        const dbIds = tags
          .flatMap((t) => t.children?.map((c) => c.dbId) || [])
          .filter(Boolean);

        await this.$refs.viewersSection?.load3dViewer?.();
        if (this.isolatedTags !== tags) return;

        if (dbIds.length) {
          this.$store.state.forgeService?.getMainExtension()?.isolateDbIds(dbIds);
        } else {
          const point = this.getPointFromTags(tags);
          if (point) {
            // Position-only tags (Scaffold / Permit / Container) have no model
            // element to isolate. Clear any previous row's isolation before
            // moving the camera to their position.
            this.$store.state.forgeService?.endIsolation3D?.();
            // getPointFromTags() returns WORLD space; zoomToPoint3dViewer feeds
            // navigation.fitBounds (VIEWER space) and builds its box with a viewer-unit
            // offset (1/unitScale), so convert. Mirrors the getPointInView() callers.
            const viewerPoint =
              this.$store.state.forgeService?.worldToView3d?.(point, point) ?? point;
            this.$store.state.forgeService?.zoomToPoint3dViewer?.(viewerPoint);
          } else {
            // Do not leave a previous row's isolation visible when the newly requested tags have
            // no model element or usable point. A stale 3D result is more dangerous than an empty
            // result because it looks like a successful match for the current row.
            this.$store.state.forgeService?.endIsolation3D?.();
            this.$dialog.notify.warning('No 3D geometry is available for the selected tags.', {
              position: 'top-right',
              timeout: 3000,
            });
          }
        }
      },

      /**
       * Register rows carry lightweight linked-tag DTOs. Fetch the full tag only
       * when that DTO has no usable 3D element, point, or position; Scaffold-family
       * tags expose their zoom target through the full tag's position property.
       */
      async hydrateTagsFor3d(tags) {
        return Promise.all(
          tags.map(async (tag) => {
            const hasSpatialData =
              tag.children?.some((child) => child.dbId || child.center) ||
              tag.point ||
              tag.position;
            if (hasSpatialData || !tag.id) return tag;

            try {
              return await this.$store.dispatch('GET_BASE_TAG_BY_ID', {
                id: tag.id,
                assetId: this.facilityId,
                expectedKind: null,
              });
            } catch (err) {
              warn('RegistersIndex', 'Failed to load full tag details for 3D view:', {
                tagId: tag.id,
                err,
              });
              return tag;
            }
          }),
        );
      },

      /**
       * Resolve the first usable point for the isolated tags, returning the
       * source tag and which field the point came from. Register LinkedTags are
       * API DTOs (ForgeBaseTagVM) that do NOT project the tag's own point/position
       * or level2D — only children[].center — so this almost always falls through
       * to 'child.center'. That distinction matters for 2D level resolution, since
       * the element's bbox center can sit at a different elevation than the tag's
       * own stored point (see logLevelResolutionDebug / isolateTagsIn2dViewer).
       */
      findPointTag(tags = this.isolatedTags) {
        for (const tag of tags) {
          if (tag.point) return { point: tag.point, tag, source: 'tag.point' };
          if (tag.position) return { point: tag.position, tag, source: 'tag.position' };
          const child = tag.children?.find(c => c.center);
          if (child?.center) return { point: child.center, tag, source: 'child.center' };
        }
        return { point: null, tag: null, source: null };
      },

      getPointFromTags(tags = this.isolatedTags) {
        return this.findPointTag(tags).point;
      },

      async isolateTagsIn2dViewer() {
        try {
          const tags = this.isolatedTags;
          if (!tags.length) return;
          const { point, tag: pointTag, source: pointSource } = this.findPointTag();
          if (!point) {
            this.$dialog?.notify?.warning?.('No position data available for 2D view');
            return;
          }
          const forgeService = this.$store.state.forgeService;
          const level = forgeService?.getLevelContainsPoint?.(point) || this.$store.state.levels?.[0];

          // [Register2D] Diagnostic: why does the store action raise "No Level
          // found"? Logs the point we actually feed to getLevelContainsPoint
          // (the linked element's child.center) vs the authoritative store tag's
          // own point/level2D, plus every level's elevation band. Reproduce the
          // bug with DevTools open and read the single [Register2D] entry.
          this.logLevelResolutionDebug({ point, pointTag, pointSource, resolvedLevel: level });

          if (!level?.planUrn) {
            this.$dialog?.notify?.warning?.('No floor plan available for this location');
            return;
          }
          this.$store.state.selectedLevel = level;
          await this.$refs.viewersSection?.load2dViewer?.(level.planUrn);
          this.$store.dispatch('zoomToPointIn2D', {
            point3d: point,
            shouldZoom: true,
            callback: () => {
              forgeService?.getLabels2DExtension?.()?.createHighlightingTags?.(tags);
            },
          });
        } catch (err) {
          error('Failed to show registers in 2D view:', err);
          this.$dialog?.notify?.error?.('Failed to show registers in 2D view');
        }
      },

      /**
       * Diagnostic for the register 2D "No Level found for the selected item"
       * error. The register only has the API LinkedTags DTO (no tag.point /
       * level2D), so it resolves the 2D plan purely from the linked element's
       * child.center elevation. The store action re-runs that same elevation
       * lookup; when child.center sits outside every level's band it returns
       * null and raises the error — whereas the main viewer resolves from the
       * tag's own point. This logs both sides so we can confirm the gap with
       * real numbers. Silent in production (debugLogger gates on debugMode).
       */
      logLevelResolutionDebug({ point, pointTag, pointSource, resolvedLevel }) {
        try {
          const forgeService = this.$store.state.forgeService;
          const levels = this.$store.state.levels || [];
          // Recover the full store tag (has the authoritative point + level2D)
          // when the facility's tags are loaded in this context — may be absent.
          const storeTag = pointTag?.id ? forgeService?.getTagById?.(pointTag.id) : null;
          const levelNameForPoint = (p) =>
            p ? (forgeService?.getLevelContainsPoint?.(p)?.name ?? null) : '(no point)';

          // child.center is viewer-space (model mm); level bands are world-space
          // (metres). view3dToWorld converts via (p + globalOffset) * unitScale —
          // BUT it silently no-ops when unitScale is unset (e.g. 2D viewer open
          // without the 3D model). Capture the converted point + lookup so we can
          // confirm the conversion actually fires and lands in a real band.
          const worldPoint = forgeService?.view3dToWorld?.(point) ?? null;

          log('[Register2D] level resolution', {
            // Where the point we use actually comes from — expect 'child.center'
            // for register tags, which is the crux of the bug.
            pointSource,
            pointUsed: point,
            pointUsedZ: point?.z ?? null,
            // Coordinate-space context — the fix hinges on unitScale being live.
            coordSpace: {
              unitScale: this.$store.state.unitScale ?? null,
              globalOffset: this.$store.state.globalOffset ?? null,
              hasViewerModel: !!this.$store.state.viewer?.model,
              childCenterAsWorld: worldPoint,
              childCenterAsWorldZ: worldPoint?.z ?? null,
            },
            // What the API DTO exposes (expect no point/position/level2D).
            dto: {
              id: pointTag?.id,
              name: pointTag?.name,
              hasPoint: !!pointTag?.point,
              hasPosition: !!pointTag?.position,
              level2D: pointTag?.level2D ?? '(none on DTO)',
            },
            // The authoritative store tag (what the main viewer uses), if loaded.
            storeTag: {
              found: !!storeTag,
              point: storeTag?.point ?? null,
              pointZ: storeTag?.point?.z ?? null,
              level2D: storeTag?.level2D ?? '(none)',
            },
            // The decisive comparison. byChildCenter fails (mm vs m); the FIX is
            // byChildCenterAsWorld — it must report a real level name, not null.
            elevationLookup: {
              byChildCenter: levelNameForPoint(point),
              byChildCenterAsWorld: levelNameForPoint(worldPoint),
              byStoreTagPoint: levelNameForPoint(storeTag?.point),
            },
            resolvedLevel: resolvedLevel
              ? {
                  name: resolvedLevel.name,
                  hasPlanUrn: !!resolvedLevel.planUrn,
                  // True when getLevelContainsPoint failed and we silently fell
                  // back to levels[0] — the case that errors in the store action.
                  fellBackToLevels0:
                    resolvedLevel === levels[0] && levelNameForPoint(point) === null,
                }
              : null,
            levels: levels.map((l) => ({
              name: l.name,
              bottomElevation: l.bottomElevation,
              topElevation: l.topElevation,
              hasPlanUrn: !!l.planUrn,
            })),
          });
        } catch (err) {
          warn('[Register2D] level resolution debug failed', err);
        }
      },

      async isolateTagsIn360Viewer() {
        /* AVA-492 explicit-viewer */
        try {
          const tag = this.isolatedTags[0];
          if (!tag) return;
          let imageToLoad = null;
          const point = this.getPointFromTags();
          if (point) {
            const forgeService = this.$store.state.forgeService;
            if (forgeService?.getClosestImageToPoint) {
              // getPointFromTags() returns a WORLD-space point (also consumed by the 2D
              // level-resolution path, which needs world space). getClosestImageToPoint
              // compares against getCameraPositionInViewer() (VIEWER space), so convert
              // here — mirrors the main-UI flow's element.getPointInView(). Fixes wrong
              // nearest-360 on facilities with a non-trivial globalOffset.
              const viewerPoint = forgeService.worldToView3d?.(point, point) ?? point;
              imageToLoad = forgeService.getClosestImageToPoint(viewerPoint);
            }
          }
          if (!imageToLoad) {
            this.$dialog?.notify?.warning?.('No 360 image found near this tag');
            return;
          }
          await this.$nextTick();
          await this.$refs.viewersSection?.loadPannellumViewer?.(imageToLoad, tag, 'pannellum-viewer');
        } catch (err) {
          error('Failed to load 360 viewer:', err);
          this.$dialog?.notify?.error?.('Failed to load 360 viewer');
        }
      },

      onCloseViewer() {
        this.viewerOpened = false;
        this.is3dOpened = false;
        this.isolatedTags = [];
        this.$store.commit('SET_VIEWER_TYPE', '2d');
        this.$store.commit('SET_ACTIVE_DOCUMENT', null);
      },

      handleEditComplete() {
        // RegisterTable handles its own data refresh after edits
      },
    },
    components: {
      ViewersSection,
      RegisterAuditSidebar,
      AuditHistoryButton,
    },
  };
</script>

<style lang="scss">
  /* Positioned ancestor + clip for the audit sidebar's overlay variant.
     Without position:relative the absolute sidebar would anchor to the viewport
     (or an unrelated ancestor); overflow:hidden guards against any residual
     horizontal spill from the viewport-relative 30vw viewer. */
  .registers-layout {
    position: relative;
    overflow: hidden;
  }

  .registers-section {
    min-width: 50%;
    flex: 1;
  }

  /* ==========================================
     REGISTER HEADER
     Matches RegisterManagement style
     ========================================== */

  .register-header {
    min-height: 65px;
    max-height: 65px;
    padding: 8px 12px;
    flex-shrink: 0;
  }

  .register-header__title {
    font-size: 20px !important;
    font-weight: 600 !important;
    line-height: 1.3 !important;
    letter-spacing: -0.01em;
  }

  .register-header__subtitle {
    font-size: 13px !important;
    color: var(--v-text_secondary-base) !important;
    margin-top: 2px;
  }

  .register-header__actions {
    flex-direction: row;
    align-items: center;
    gap: 8px;
    margin: 0;
  }
</style>
